import { useMemo } from 'react';
import { CropAndBrandAvailability7 } from '../../Models/Responses/SeedResponseV7';
import { RootState } from '../../store/Store';
import { BagSizeProvider } from './BagSizeProvider';
import { PricingProvider } from './PricingProvider';
import { ProductRollupCalc } from './ProductRollupCalc';
import { IProductRollupCalcLogger, RelatedObjects } from './ProductRollupCalcLogger';

// Trace logging calculation details is disabled by default.
const enableDebugLogging = false;

/** 
 * Sets up a product rollup calculator for use in UI components. 
 * Make sure loading is false before use.
 */
export const useProductRollup = (seeds: CropAndBrandAvailability7[]) => 
{
	const logger = useMemo(() => enableDebugLogging ? new ConsoleLogger() : undefined, []);

	const { pricer, bagSizer, calculator } = useMemo(() => 
	{
		const pricer = new PricingProvider(seeds, logger);
		const bagSizer = new BagSizeProvider();
		const calculator = new ProductRollupCalc(pricer, bagSizer, logger);

		return {
			pricer,
			bagSizer,
			calculator,
		};
	}, [seeds, logger]);	

	return {
		calculator,
		pricer,
		bagSizer,
	};
};

/** Generate a new calculator from the redux root state. */
export const calculatorFromStore = (state: RootState) => 
{
	const seeds = state.seeds.allSeedsV7;

	if (!seeds)
		throw new Error('Attempting to create a calculator without seed data.');

	// Trace logging is disabled by default.
	const logger = enableDebugLogging ? new ConsoleLogger() : undefined;

	const pricer =  new PricingProvider(seeds, logger);
	const bagSizer =  new BagSizeProvider();
	const calculator =  new ProductRollupCalc(pricer, bagSizer, logger);

	return { calculator, };
};

/** Primarily for debugging and only for use with the product rollup calculator. */
class ConsoleLogger implements IProductRollupCalcLogger
{
	public trace(message: string, relatedObjects?: RelatedObjects): void 
	{
		if (relatedObjects)
			console.log(message, relatedObjects);
		else
			console.log(message);
	}
}